import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

import { LockedControl } from '../LockedControl';
import styles from './style';

// Same values as user.feedPreference in the app: the choice follows the user on every device.
// Like the app, the menu only offers the other feed.
const FEED_PREFERENCES = ['forYou', 'following'];

interface FeedPreferenceTabMenuProps {
  value: string;
  onChange: (value: string) => void;
  // A visitor only has "Pour toi": the menu is locked and its click asks to log in.
  onLocked?: () => void;
}

const FeedPreferenceTabMenu: React.FC<FeedPreferenceTabMenuProps> = React.memo((props) => {
  const { value, onChange, onLocked } = props;
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const close = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onPick = useCallback((preference: string) => {
    setAnchorEl(null);
    if (preference !== value) {
      onChange(preference);
    }
  }, [onChange, value]);

  const chevronSx = useMemo(() => {
    if (anchorEl) {
      return { ...styles.chevron, ...styles.chevronOpen };
    }
    return styles.chevron;
  }, [anchorEl]);

  if (onLocked) {
    return (
      <LockedControl tooltip={t('admin.guest.reasons.feedTabs.title')} onClick={onLocked}>
        <Button disableRipple sx={styles.button}>
          {t(`admin.feed.${value}`)}
          <KeyboardArrowDown className="locked-chevron" sx={chevronSx} />
        </Button>
      </LockedControl>
    );
  }

  return (
    <>
      <Button disableRipple onClick={open} sx={styles.button}>
        {t(`admin.feed.${value}`)}
        <KeyboardArrowDown sx={chevronSx} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={styles.anchorOrigin}
        transformOrigin={styles.transformOrigin}
        PaperProps={styles.paperProps}
      >
        {FEED_PREFERENCES.filter((preference) => preference !== value).map((preference) => (
          <MenuItem key={preference} onClick={() => onPick(preference)} sx={styles.item}>
            {t(`admin.feed.${preference}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

export { FEED_PREFERENCES };

export default FeedPreferenceTabMenu;
